import { useCallback, useEffect, useRef, useState } from "react";
import api, { errMsg } from "../lib/api";
import { toast } from "../lib/notices";
import { formatDateTime } from "../lib/datetime";
import VoicePreviewButton from "./VoicePreviewButton";
import { Trash2, Mic, Square, Upload, Wand2, Loader2, X } from "lucide-react";

/*
 * The account-level home for cloned voices.
 *
 * A voiceprint belongs to the account, not to the video it was first recorded
 * for (see `voice_ledger.list_for_user` — ownership has always been keyed on
 * the user), so it is listed, enrolled and destroyed here rather than buried
 * in one project's wizard. The per-project clone flow in NewProject still
 * exists; this is the same ledger seen from the account side.
 *
 * Enrolment posts to Pravaani only. It is the one provider whose clone is a
 * prompt we store ourselves, so it is the one that can be created without a
 * project to hang it on; ElevenLabs and Muapi clones are still made inside a
 * project. Keeping that a named constant rather than a picker avoids offering
 * a provider the endpoint would reject.
 */
const ENROL_PROVIDER = "pravaani";

const FALLBACK_LANGS = [
  { code: "en", label: "English", region: "Global" },
  { code: "en-IN", label: "English (India)", region: "Indic" },
  { code: "hi-IN", label: "Hindi", region: "Indic" },
];

// A voice is identified by the (provider, id) pair, not the id alone — the
// delete route is keyed on both, and two providers may reuse an id.
const keyOf = (v) => `${v.provider}:${v.voice_id}`;

export default function VoiceLibrary() {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  // Which row has its confirm panel open, and which is mid-delete. Both are
  // the (provider, id) key rather than a boolean, so two rows can never share
  // one confirmation.
  const [confirming, setConfirming] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const [languages, setLanguages] = useState(FALLBACK_LANGS);
  const [language, setLanguage] = useState("en");
  const [subject, setSubject] = useState("");
  const [refText, setRefText] = useState("");
  const [consent, setConsent] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [audioLabel, setAudioLabel] = useState("");
  const [recording, setRecording] = useState(false);
  const [enrolling, setEnrolling] = useState(false);

  const mediaRec = useRef(null);
  const chunks = useRef([]);
  const streamRef = useRef(null);
  const fileInput = useRef(null);
  const confirmBtn = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/providers/my-voices");
      setVoices(Array.isArray(r.data?.voices) ? r.data.voices : []);
      setLoadError("");
    } catch (e) {
      setLoadError(errMsg(e, "Could not load your voices"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/providers/languages")
      .then((r) => { if (r.data?.languages?.length) setLanguages(r.data.languages); })
      .catch(() => {});
  }, []);

  // A blob URL outlives the blob unless it is revoked, and this panel can
  // produce a new take on every click of Record.
  useEffect(() => () => { if (audioUrl) URL.revokeObjectURL(audioUrl); }, [audioUrl]);
  // Leaving the page mid-recording must not leave the mic light on.
  useEffect(() => () => { streamRef.current?.getTracks().forEach((t) => t.stop()); }, []);

  // The confirm panel is announced by role="alert", but a keyboard user also
  // has to *reach* it: it renders below the button they just pressed, so move
  // focus into it. Keyed on which row is open, not on every render, or the
  // "Deleting…" state change would yank focus back mid-press.
  useEffect(() => { if (confirming) confirmBtn.current?.focus(); }, [confirming]);

  const attach = (blob, label) => {
    setAudioBlob(blob);
    setAudioUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
    setAudioLabel(label);
  };

  const clearAudio = () => {
    setAudioBlob(null);
    setAudioUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return ""; });
    setAudioLabel("");
    if (fileInput.current) fileInput.current.value = "";
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunks.current = [];
      rec.ondataavailable = (e) => chunks.current.push(e.data);
      rec.onstop = () => {
        attach(new Blob(chunks.current, { type: "audio/webm" }), "Recorded just now");
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      };
      mediaRec.current = rec;
      streamRef.current = stream;
      rec.start();
      setRecording(true);
    } catch (e) { toast.error("Microphone access denied"); }
  };
  const stopRec = () => { mediaRec.current?.stop(); setRecording(false); };

  const pickFile = (files) => {
    const f = files?.[0];
    if (!f) return;
    attach(f, f.name);
  };

  const canSubmit = !!audioBlob && consent && !!subject.trim() && !enrolling;

  const enrol = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setEnrolling(true);
    try {
      const who = subject.trim();
      const fd = new FormData();
      fd.append("file", audioBlob, audioBlob.name || "voice-sample.webm");
      const r = await api.post("/providers/my-voices", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params: {
          provider: ENROL_PROVIDER,
          name: who,
          consent_subject: who,
          ref_text: refText.trim(),
          language,
        },
      });
      toast.success(`Voice enrolled as "${r.data?.name || who}"`);
      clearAudio();
      setSubject(""); setRefText(""); setConsent(false);
      load();
    } catch (err) {
      toast.error(errMsg(err, "Could not enrol that voice"));
    } finally { setEnrolling(false); }
  };

  const del = async (v) => {
    const k = keyOf(v);
    setDeleting(k);
    try {
      await api.delete(`/providers/my-voices/${encodeURIComponent(v.provider)}/${encodeURIComponent(v.voice_id)}`);
      toast.success("Voice deleted");
      setConfirming(null);
      load();
    } catch (err) {
      toast.error(errMsg(err, "Could not delete that voice"));
    } finally { setDeleting(null); }
  };

  return (
    <section className="mt-12" data-testid="voice-library">
      <h2 className="marquee-tag mb-2">// YOUR VOICES</h2>
      <p className="text-ink-muted max-w-2xl mb-6">
        A cloned voice belongs to your account, not to one video — enrol it once here and narrate
        any project with it. We keep the consent record beside the voiceprint.
      </p>

      <form onSubmit={enrol} className="brutal-card mb-8 space-y-4" data-testid="voice-enrol-form">
        <h3 className="font-display font-bold type-subheading">Enrol a new voice</h3>

        <div>
          <span className="brutal-label" id="voice-sample-label">Voice sample</span>
          <div className="flex flex-wrap items-center gap-2 mt-1" aria-labelledby="voice-sample-label">
            {recording ? (
              <button type="button" onClick={stopRec} className="brutal-btn" data-testid="voice-stop-rec">
                <Square size={14} /> Stop recording
              </button>
            ) : (
              <button type="button" onClick={startRec} className="brutal-btn brutal-btn-secondary" data-testid="voice-start-rec">
                <Mic size={14} /> Record
              </button>
            )}
            <button type="button" onClick={() => fileInput.current?.click()}
              className="brutal-btn brutal-btn-secondary" data-testid="voice-pick-file">
              <Upload size={14} /> Upload audio
            </button>
            <input ref={fileInput} type="file" accept="audio/*" className="sr-only"
              data-testid="voice-file-input" aria-label="Upload an audio file of the voice"
              onChange={(e) => pickFile(e.target.files)} />
          </div>
          <p className="text-xs text-ink-muted mt-2">
            Around 30 seconds of clear, uninterrupted speech is enough. Background music or a second
            speaker in the clip will end up in the voice.
          </p>
          {recording && (
            <p className="text-xs text-state-warning-ink mt-2" role="status" data-testid="voice-recording-status">
              Recording — speak now, then press Stop.
            </p>
          )}
          {audioUrl && (
            <div className="brutal-card brutal-card-flat bg-surface mt-3 flex items-center gap-3" data-testid="voice-sample-preview">
              <audio controls src={audioUrl} className="flex-1" data-testid="voice-sample-audio" />
              <span className="text-xs text-ink-muted hidden sm:block">{audioLabel}</span>
              <button type="button" onClick={clearAudio} className="brutal-btn brutal-btn-ghost !p-2"
                data-testid="voice-clear-sample" aria-label="Discard this voice sample">
                <X size={14} />
              </button>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="brutal-label" htmlFor="voice-subject">Whose voice is this?</label>
            <input id="voice-subject" className="brutal-input" data-testid="voice-subject"
              value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="Full name" autoComplete="off"
              aria-describedby="voice-subject-hint" />
            <p className="text-xs text-ink-muted mt-1" id="voice-subject-hint">
              Used as the voice's name and stored as the consent record.
            </p>
          </div>
          <div>
            <label className="brutal-label" htmlFor="voice-language">Language spoken in the clip</label>
            <select id="voice-language" className="brutal-select" data-testid="voice-language"
              value={language} onChange={(e) => setLanguage(e.target.value)}>
              {languages.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="brutal-label" htmlFor="voice-ref-text">What is said in the clip? (optional)</label>
          <textarea id="voice-ref-text" className="brutal-input min-h-[80px]" data-testid="voice-ref-text"
            value={refText} onChange={(e) => setRefText(e.target.value)}
            aria-describedby="voice-ref-text-hint"
            placeholder="Leave blank and we'll transcribe it for you." />
          <p className="text-xs text-ink-muted mt-1" id="voice-ref-text-hint">
            The model aligns the audio against this transcript. A wrong transcript produces a
            worse voice than a short one.
          </p>
        </div>

        <div className="brutal-card brutal-card-flat bg-surface" data-testid="voice-consent">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" className="mt-1 w-4 h-4" data-testid="voice-consent-check"
              checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <span className="text-xs leading-relaxed">
              This is my own voice, or I hold the recorded permission of the person speaking. I
              understand a voice model will be created and stored on this account until I delete it.
            </span>
          </label>
        </div>

        <button type="submit" disabled={!canSubmit} className="brutal-btn" data-testid="voice-enrol-submit">
          {enrolling ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
          {enrolling ? "Enrolling…" : "Enrol voice"}
        </button>
      </form>

      {loadError ? (
        <div className="brutal-card text-state-danger" role="alert" data-testid="voices-error">{loadError}</div>
      ) : loading ? (
        <div className="brutal-card text-center py-10 text-ink-muted" data-testid="voices-loading">Loading your voices…</div>
      ) : voices.length === 0 ? (
        <div className="brutal-card text-center py-10 text-ink-muted" data-testid="voices-empty">
          No cloned voices yet. Enrol one above, or clone from a recording while creating a project.
        </div>
      ) : (
        <div className="grid gap-3" data-testid="voices-list">
          {voices.map((v) => {
            const k = keyOf(v);
            const open = confirming === k;
            const label = v.name || v.voice_id;
            return (
              <div key={k} className="brutal-card" data-testid={`voice-${v.voice_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display font-bold type-subheading break-all">{label}</h3>
                      <span className="brutal-chip">{v.provider}</span>
                    </div>
                    <div className="text-xs text-ink-muted mt-1">
                      Enrolled {formatDateTime(v.created_at)}
                    </div>
                    {(v.consent_subject || v.consent_granted_by) && (
                      <div className="text-xs text-ink-muted mt-1" data-testid={`voice-consent-${v.voice_id}`}>
                        Consent: {v.consent_subject || "—"}, authorised by {v.consent_granted_by || "—"}
                      </div>
                    )}
                  </div>
                  <VoicePreviewButton provider={v.provider} voiceId={v.voice_id}
                    language={language} label={label} />
                  <button onClick={() => setConfirming(open ? null : k)}
                    className="brutal-btn brutal-btn-secondary shrink-0"
                    data-testid={`btn-delete-voice-${v.voice_id}`}
                    aria-expanded={open}
                    aria-label={open ? `Cancel deleting voice ${label}` : `Delete voice ${label}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
                {open && (
                  <div className="brutal-card brutal-card-flat bg-surface mt-3" role="alert"
                    data-testid={`voice-confirm-${v.voice_id}`}>
                    <p className="text-xs leading-relaxed">
                      Delete <b>{label}</b> permanently? This destroys the voice model itself — it
                      cannot be restored, and re-creating it needs a new recording. Any project
                      currently narrated by this voice reverts to a stock voice on its next render.
                    </p>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => del(v)} disabled={deleting === k}
                        className="brutal-btn" data-testid={`btn-confirm-delete-voice-${v.voice_id}`}
                        ref={confirmBtn}>
                        {deleting === k ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        {deleting === k ? "Deleting…" : "Delete permanently"}
                      </button>
                      <button onClick={() => setConfirming(null)} disabled={deleting === k}
                        className="brutal-btn brutal-btn-secondary"
                        data-testid={`btn-cancel-delete-voice-${v.voice_id}`}>
                        Keep it
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
